import { useEffect, useState, useCallback } from "react";
import { Skull, Circle, Plus, X, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { inputCls, Label } from "./shared";

const FRAG_RARITIES = ["rare", "legendary"];
const ITEM_RARITIES = ["rare", "elite", "legendary"];
const FRAG_KINDS = ["weapon", "armor"];
const ITEM_KINDS = ["weapon", "offhand", "armor", "helmet"];

const NEW_REWARD = {
  fragments: { type: "fragments", rarity: "legendary", kind: "weapon", amount: 10 },
  item: { type: "item", rarity: "rare", kind: "weapon", amount: 1 },
  gold: { type: "gold", amount: 100 },
  scouting: { type: "scouting", days: 7 },
  training_boost: { type: "training_boost", percent: 10, days: 2 },
  campaign_stamina: { type: "campaign_stamina", amount: 1 },
  recovery_ether: { type: "recovery_ether", amount: 1 },
};

function rewardText(r) {
  if (r.type === "fragments") return `${r.amount} ${r.rarity} ${r.kind} frag${r.amount !== 1 ? "s" : ""}`;
  if (r.type === "item") return `${r.amount}× ${r.rarity} ${r.kind}`;
  if (r.type === "gold") return `${r.amount} gold`;
  if (r.type === "scouting") return `2× scouting for ${r.days}d`;
  if (r.type === "training_boost") return `+${r.percent}% training for ${r.days}d`;
  if (r.type === "campaign_stamina") return `${r.amount} Campaign Stamina Potion${r.amount !== 1 ? "s" : ""}`;
  if (r.type === "recovery_ether") return `${r.amount} Recovery Ether${r.amount !== 1 ? "s" : ""}`;
  return "";
}
const rewardsSummary = (rs) => (rs.length ? rs.map(rewardText).join(", ") : "no rewards");

function RewardRow({ r, onChange, onRemove }) {
  const num = (k, min = 1) => (
    <input type="number" min={min} data-testid={`reward-${r.type}-${k}`} className={inputCls + " w-24"} value={r[k]}
      onChange={(e) => onChange({ ...r, [k]: Number(e.target.value) })} />
  );
  const sel = (k, opts) => (
    <select className={inputCls + " w-32"} data-testid={`reward-${r.type}-${k}`} value={r[k]} onChange={(e) => onChange({ ...r, [k]: e.target.value })}>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return (
    <div data-testid={`reward-row-${r.type}`} className="flex flex-wrap items-center gap-2 bg-black/30 border border-zinc-800 rounded-sm px-2.5 py-2">
      <span className="text-[11px] uppercase tracking-widest text-amber-400 w-24">{r.type.replace("_", " ")}</span>
      {r.type === "fragments" && <>{num("amount")} {sel("rarity", FRAG_RARITIES)} {sel("kind", FRAG_KINDS)}</>}
      {r.type === "item" && <>{num("amount")} {sel("rarity", ITEM_RARITIES)} {sel("kind", ITEM_KINDS)}</>}
      {r.type === "gold" && <>{num("amount")} <span className="text-xs text-zinc-500">gold</span></>}
      {r.type === "scouting" && <><span className="text-xs text-zinc-400">2× for</span> {num("days")} <span className="text-xs text-zinc-500">days</span></>}
      {r.type === "training_boost" && <>+{num("percent")}<span className="text-xs text-zinc-500">% for</span> {num("days")} <span className="text-xs text-zinc-500">days</span></>}
      {r.type === "campaign_stamina" && <>{num("amount")} <span className="text-xs text-zinc-500">potions</span></>}
      {r.type === "recovery_ether" && <>{num("amount")} <span className="text-xs text-zinc-500">ethers</span></>}
      <button type="button" data-testid="reward-remove" onClick={onRemove} className="ml-auto text-zinc-500 hover:text-red-400"><X size={15} /></button>
    </div>
  );
}

export function BossEventPanel() {
  const [ev, setEv] = useState(null);
  const [name, setName] = useState("The Wild Beast");
  const [hp, setHp] = useState("");
  const [useTiers, setUseTiers] = useState(false); // Toggle between legacy and multi-tier
  const [rewardMode, setRewardMode] = useState("strikes"); // Legacy mode
  const [topX, setTopX] = useState("10"); // Legacy top X
  const [rewards, setRewards] = useState([]); // Legacy rewards
  const [tiers, setTiers] = useState([
    { mode: "strikes", min_strikes: 5, rewards: [] }
  ]); // Multi-tier rewards
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/boss").then((r) => {
    setEv(r.data);
    if (!r.data.active && r.data.suggested_hp) setHp(String(r.data.suggested_hp));
    setRewards(r.data.last_rewards || r.data.default_rewards || []);
  }).catch(() => setEv({ active: false })), []);
  useEffect(() => { load(); }, [load]);

  const addReward = (type) => setRewards((rs) => [...rs, { ...NEW_REWARD[type] }]);
  const updateReward = (i, r) => setRewards((rs) => rs.map((x, j) => (j === i ? r : x)));
  const removeReward = (i) => setRewards((rs) => rs.filter((_, j) => j !== i));

  // Multi-tier functions
  const addTier = () => setTiers((ts) => [...ts, { mode: "strikes", min_strikes: 5, rewards: [] }]);
  const removeTier = (i) => setTiers((ts) => ts.filter((_, j) => j !== i));
  const updateTier = (i, field, value) => setTiers((ts) => ts.map((t, j) => j === i ? { ...t, [field]: value } : t));
  const addTierReward = (tierIdx, type) => setTiers((ts) => ts.map((t, i) => i === tierIdx ? { ...t, rewards: [...t.rewards, { ...NEW_REWARD[type] }] } : t));
  const updateTierReward = (tierIdx, rewardIdx, reward) => setTiers((ts) => ts.map((t, i) => i === tierIdx ? { ...t, rewards: t.rewards.map((r, j) => j === rewardIdx ? reward : r) } : t));
  const removeTierReward = (tierIdx, rewardIdx) => setTiers((ts) => ts.map((t, i) => i === tierIdx ? { ...t, rewards: t.rewards.filter((_, j) => j !== rewardIdx) } : t));

  const validate = () => {
    if (!(Number(hp) > 0)) { toast.error("Enter HP greater than 0"); return false; }
    if (!name.trim()) { toast.error("Enter a beast name"); return false; }
    
    if (useTiers) {
      if (tiers.length === 0) { toast.error("Add at least one reward tier"); return false; }
      if (tiers.some(t => t.rewards.length === 0)) { toast.error("Each tier must have at least one reward"); return false; }
    } else {
      if (rewards.length === 0) { toast.error("Add at least one reward"); return false; }
    }
    return true;
  };
  
  const doSpawn = async () => {
    setBusy(true);
    try {
      const payload = { 
        name: name.trim(), 
        max_hp: Number(hp)
      };
      
      if (useTiers) {
        payload.reward_tiers = tiers;
      } else {
        payload.reward_mode = rewardMode;
        payload.reward_top_x = Number(topX) || 10;
        payload.rewards = rewards;
      }
      
      const r = await api.post("/admin/boss/spawn", payload);
      toast.success(`${r.data.name} unleashed with ${r.data.max_hp.toLocaleString()} HP`);
      setConfirming(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  
  const stop = async () => {
    setBusy(true);
    try { await api.post("/admin/boss/end"); toast.success("Community event ended"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const pct = ev?.active ? Math.round((ev.hp / Math.max(ev.max_hp, 1)) * 100) : 0;

  return (
    <div data-testid="boss-event-panel" className={`mb-6 border p-4 rounded-sm ${ev?.active ? "border-red-500/60 bg-red-950/20" : "border-amber-900/30 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Skull size={16} className="text-red-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Community Beast Event</h3>
        {ev?.active && (
          <span data-testid="boss-event-active-badge" className="ml-auto flex items-center gap-1.5 text-xs text-red-300 font-semibold">
            <Circle size={9} className="fill-red-400 text-red-400 animate-pulse" /> Active · {pct}% HP
          </span>
        )}
      </div>
      {ev?.active ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-400 flex-1 min-w-[220px]">
            <span className="text-red-300 font-semibold font-cinzel">{ev.name}</span> is loose —
            <span className="text-zinc-200 tabular-nums"> {ev.hp.toLocaleString()} / {ev.max_hp.toLocaleString()} HP</span>
            <span className="text-zinc-500"> · {ev.participants} fighter{ev.participants === 1 ? "" : "s"} joined</span>
            <br />
            <span className="text-amber-400/90 text-xs" data-testid="boss-event-reward-rule">
              Reward rule: {ev.reward_mode === "top_damage" ? `Top ${ev.reward_top_x} by damage` : ev.reward_mode === "top_hits" ? `Top ${ev.reward_top_x} by hits` : "Everyone with 5+ strikes"}
            </span>
            <br />
            <span className="text-emerald-400/90 text-xs" data-testid="boss-event-active-rewards"><Gift size={11} className="inline mr-1" />Spoils: {ev.rewards_text}</span>
          </p>
          <button data-testid="boss-event-stop-btn" onClick={stop} disabled={busy}
            className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm disabled:opacity-50">End Event Now</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-sm text-zinc-500 w-full mb-1">Unleash a server-wide co-op boss. Players strike it once every 3h. Choose how the spoils are shared and exactly what each slayer wins. Suggested HP scales to ~{ev?.active_players || 1} active players for a ~7-day kill.</p>
          <div className="flex-1 min-w-[180px]"><Label>Beast Name</Label>
            <input data-testid="boss-event-name" className={inputCls} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} /></div>
          <div className="w-40"><Label>Total HP</Label>
            <input data-testid="boss-event-hp" type="number" min="1" step="100" className={inputCls} value={hp} onChange={(e) => setHp(e.target.value)} /></div>
          {ev?.suggested_hp ? <button type="button" onClick={() => setHp(String(ev.suggested_hp))} className="text-[11px] text-amber-400 hover:text-amber-300 underline pb-2">use suggested {ev.suggested_hp.toLocaleString()}</button> : null}
          
          {/* Toggle for multi-tier system */}
          <div className="w-full mt-2 border-t border-zinc-800 pt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={useTiers} 
                onChange={(e) => setUseTiers(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-zinc-300">Use Multi-Tier Rewards (stackable bonuses)</span>
            </label>
            <p className="text-xs text-zinc-600 mt-1 ml-6">Allow players to earn multiple rewards by meeting different criteria simultaneously</p>
          </div>

          {!useTiers ? (
            /* Legacy single-tier UI */
            <>
              <div className="w-52"><Label>Reward Mode</Label>
                <select data-testid="boss-event-reward-mode" className={inputCls} value={rewardMode} onChange={(e) => setRewardMode(e.target.value)}>
                  <option value="strikes">Everyone with 5+ strikes</option>
                  <option value="top_damage">Top X by most damage</option>
                  <option value="top_hits">Top X by most hits</option>
                </select></div>
              {rewardMode !== "strikes" && (
                <div className="w-28"><Label>Top X</Label>
                  <input data-testid="boss-event-top-x" type="number" min="1" max="1000" className={inputCls} value={topX} onChange={(e) => setTopX(e.target.value)} /></div>
              )}

              {/* Reward builder */}
              <div className="w-full mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Label>Kill Rewards (each eligible slayer receives all)</Label>
                  <select data-testid="add-reward-select" className={inputCls + " w-auto ml-auto"} value="" onChange={(e) => { if (e.target.value) { addReward(e.target.value); e.target.value = ""; } }}>
                    <option value="">+ Add reward…</option>
                    <option value="fragments">Fragments</option>
                    <option value="item">Forged item</option>
                    <option value="gold">Gold</option>
                    <option value="scouting">2× scouting (7d)</option>
                    <option value="training_boost">Training boost</option>
                    <option value="campaign_stamina">Campaign Stamina Potion</option>
                    <option value="recovery_ether">Recovery Ether</option>
                  </select>
                </div>
                <div className="space-y-2" data-testid="reward-list">
                  {rewards.length === 0 ? <p className="text-xs text-zinc-600">No rewards yet — add at least one.</p>
                    : rewards.map((r, i) => <RewardRow key={i} r={r} onChange={(nr) => updateReward(i, nr)} onRemove={() => removeReward(i)} />)}
                </div>
              </div>
            </>
          ) : (
            /* Multi-tier UI */
            <div className="w-full mt-2 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Label>Reward Tiers (players can qualify for multiple)</Label>
                <button 
                  onClick={addTier}
                  className="ml-auto px-3 py-1 text-xs bg-amber-900/30 text-amber-400 border border-amber-700/50 rounded hover:bg-amber-900/50"
                >
                  + Add Tier
                </button>
              </div>
              
              {tiers.map((tier, tierIdx) => (
                <div key={tierIdx} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-amber-400 font-cinzel text-sm">Tier {tierIdx + 1}</span>
                    {tiers.length > 1 && (
                      <button 
                        onClick={() => removeTier(tierIdx)}
                        className="ml-auto text-xs text-red-400 hover:text-red-300"
                      >
                        Remove Tier
                      </button>
                    )}
                  </div>
                  
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <Label>Mode</Label>
                      <select 
                        className={inputCls} 
                        value={tier.mode} 
                        onChange={(e) => updateTier(tierIdx, 'mode', e.target.value)}
                      >
                        <option value="strikes">Everyone with X+ strikes</option>
                        <option value="top_damage">Top X by most damage</option>
                        <option value="top_hits">Top X by most hits</option>
                      </select>
                    </div>
                    
                    {tier.mode === "strikes" ? (
                      <div className="w-32">
                        <Label>Min Strikes</Label>
                        <input 
                          type="number" 
                          min="1" 
                          max="100" 
                          className={inputCls} 
                          value={tier.min_strikes || 5} 
                          onChange={(e) => updateTier(tierIdx, 'min_strikes', Number(e.target.value))}
                        />
                      </div>
                    ) : (
                      <div className="w-32">
                        <Label>Top X</Label>
                        <input 
                          type="number" 
                          min="1" 
                          max="1000" 
                          className={inputCls} 
                          value={tier.top_x || 10} 
                          onChange={(e) => updateTier(tierIdx, 'top_x', Number(e.target.value))}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label className="text-xs">Tier {tierIdx + 1} Rewards</Label>
                      <select 
                        className={inputCls + " w-auto ml-auto text-xs"} 
                        value="" 
                        onChange={(e) => { 
                          if (e.target.value) { 
                            addTierReward(tierIdx, e.target.value); 
                            e.target.value = ""; 
                          } 
                        }}
                      >
                        <option value="">+ Add reward…</option>
                        <option value="fragments">Fragments</option>
                        <option value="item">Forged item</option>
                        <option value="gold">Gold</option>
                        <option value="scouting">2× scouting (7d)</option>
                        <option value="training_boost">Training boost</option>
                        <option value="campaign_stamina">Campaign Stamina Potion</option>
                        <option value="recovery_ether">Recovery Ether</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      {tier.rewards.length === 0 ? (
                        <p className="text-xs text-zinc-600">No rewards yet — add at least one.</p>
                      ) : (
                        tier.rewards.map((r, rIdx) => (
                          <RewardRow 
                            key={rIdx} 
                            r={r} 
                            onChange={(nr) => updateTierReward(tierIdx, rIdx, nr)} 
                            onRemove={() => removeTierReward(tierIdx, rIdx)} 
                          />
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {confirming ? (
            <div className="w-full mt-2 border border-amber-600/50 bg-amber-950/20 rounded-sm p-3" data-testid="boss-confirm-panel">
              <p className="text-sm text-amber-200 mb-1 font-cinzel">Confirm unleashing <span className="text-red-300">{name}</span> ({Number(hp).toLocaleString()} HP)?</p>
              {!useTiers ? (
                <p className="text-xs text-zinc-300 mb-3"><Gift size={12} className="inline mr-1 text-emerald-400" />Each eligible slayer wins: <span className="text-emerald-300">{rewardsSummary(rewards)}</span></p>
              ) : (
                <div className="text-xs text-zinc-300 mb-3">
                  <Gift size={12} className="inline mr-1 text-emerald-400" />
                  <span className="text-emerald-300">{tiers.length} reward tier{tiers.length === 1 ? '' : 's'} - players can stack multiple bonuses</span>
                </div>
              )}
              <div className="flex gap-2">
                <button data-testid="boss-confirm-btn" onClick={doSpawn} disabled={busy}
                  className="px-5 py-2 bg-red-600 text-white font-cinzel uppercase tracking-wide text-sm hover:bg-red-500 disabled:opacity-50 rounded-sm">Confirm & Unleash</button>
                <button onClick={() => setConfirming(false)} disabled={busy} className="px-4 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-500 text-sm rounded-sm">Back</button>
              </div>
            </div>
          ) : (
            <button data-testid="boss-event-start-btn" onClick={() => { if (validate()) setConfirming(true); }} disabled={busy}
              className="px-5 py-2 bg-red-600 text-white font-cinzel uppercase tracking-wide text-sm hover:bg-red-500 disabled:opacity-50 rounded-sm flex items-center gap-1.5"><Plus size={15} /> Review & Start</button>
          )}
        </div>
      )}
    </div>
  );
}
